import type { DuplicateApplicationJob, Job } from '@/api/types'
import { isJobFailed, isJobRunning } from './polling'

function isDuplicateOf(job: Job, applicationId: number): job is DuplicateApplicationJob {
  return (
    job.type === 'duplicate_application' && job.original_application?.id === applicationId
  )
}

/**
 * The duplicate job worth showing on an application's row, if there is one.
 *
 * Running jobs always count. Failed ones only count if they failed *since this
 * page was opened*, which is the difference between "the thing you just started
 * went wrong" and "something went wrong last Tuesday". The job list is the
 * user's recent jobs, so without that cutoff a week-old failure would sit on the
 * sidebar until it aged out of the endpoint's page.
 *
 * A cancelled or finished job shows nothing: the sidebar already has the answer,
 * because the duplicate arrives over the websocket as an `application_created`.
 */
export function duplicateJobFor(
  jobs: readonly Job[],
  applicationId: number,
  since: number,
): DuplicateApplicationJob | undefined {
  const relevant = jobs
    .filter((job): job is DuplicateApplicationJob => isDuplicateOf(job, applicationId))
    .filter(
      (job) =>
        isJobRunning(job) || (isJobFailed(job) && Date.parse(job.updated_on) >= since),
    )

  // The most recently touched, so duplicating twice does not leave the first
  // attempt's progress on the row.
  return relevant.sort((a, b) => Date.parse(b.updated_on) - Date.parse(a.updated_on))[0]
}
